const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Centralised URL builders for the HRV-4 backend.
 *
 * Mirrors the shape of the mobile api.ts so the same endpoint names work
 * across both platforms. Components should never hard-code paths — always
 * go through this object, which means we can change the backend routing in
 * one place if it ever needs to move.
 */
export const API = {
  // Collection
  users: () => `${API_BASE_URL}/user`,
  activities: () => `${API_BASE_URL}/activities`,

  // Auth
  register: () => `${API_BASE_URL}/user/register`,
  login: () => `${API_BASE_URL}/user/login`,
  logout: () => `${API_BASE_URL}/user/logout`,
  refresh: () => `${API_BASE_URL}/user/refresh`,

  // User CRUD
  userById: (id) => `${API_BASE_URL}/user/${id}`,
  userByEmail: (email) =>
    `${API_BASE_URL}/user/by-email?email=${encodeURIComponent(email)}`,
  updateUser: (id) => `${API_BASE_URL}/user/${id}`,
  deleteUser: (id) => `${API_BASE_URL}/user/${id}`,
  activitiesByUser: (userId) => `${API_BASE_URL}/activities/user/${userId}`,

  // Activities
  activityById: (id) => `${API_BASE_URL}/activities/${id}`,

  // Security
  changePassword: (id) => `${API_BASE_URL}/user/${id}/change-password`,

  // Single-field partial updates (PATCH). Handy when you don't want to send
  // the full UserUpdateDto from the client.
  patchFirstName: (id) => `${API_BASE_URL}/user/${id}/first-name`,
  patchLastName: (id) => `${API_BASE_URL}/user/${id}/last-name`,
  patchAge: (id) => `${API_BASE_URL}/user/${id}/age`,
  patchGender: (id) => `${API_BASE_URL}/user/${id}/gender`,
  patchClinicalStory: (id) => `${API_BASE_URL}/user/${id}/clinical-story`,
  patchNotes: (id) => `${API_BASE_URL}/user/${id}/notes`,
  patchHeight: (id) => `${API_BASE_URL}/user/${id}/height`,
  patchWeight: (id) => `${API_BASE_URL}/user/${id}/weight`,
  patchSleepGoal: (id) => `${API_BASE_URL}/user/${id}/sleep-goal`,
  // Replaces the old patchActivityLevel — backend now splits it into two:
  //   - typicalDay: "Mostly Sitting" | "Mostly Standing" | "Mostly Moving"
  //   - trainingBackground: Integer bucket (10 / 20 / 30 / 40 / 50 / 60)
  patchTypicalDay: (id) => `${API_BASE_URL}/user/${id}/typical-day`,
  patchTrainingBackground: (id) => `${API_BASE_URL}/user/${id}/training-background`,

  //chatbot
  chatbot: () => `${API_BASE_URL}/chatbot/chat`,

    modelInit: () => `${API_BASE_URL}/models/init`,
  modelGraphOutput: (range) =>
    `${API_BASE_URL}/models/output/graphs?range=${encodeURIComponent(range)}`,
  modelOverview: () => `${API_BASE_URL}/models/output/overview`,

   sensorHrWindow: () => `${API_BASE_URL}/sensor/hr/window`,
  sensorHrStream: () => `${API_BASE_URL}/sensor/hr/stream`,
  sensorStressScore: () => `${API_BASE_URL}/processed_data/stress_score`,

};

/**
 * Returns cached auth values from localStorage.
 * Handles the edge case where accessToken was accidentally saved as a JSON
 * blob (a prior version of Profile.js did this).
 */
export function getAuth() {
  try {
    const rawToken = localStorage.getItem('accessToken');
    let token = rawToken;
    if (rawToken && rawToken.startsWith('{')) {
      try {
        const parsed = JSON.parse(rawToken);
        token = parsed.accessToken || parsed.access_token || parsed.token || rawToken;
      } catch {
        /* leave as-is */
      }
    }
    const userId = localStorage.getItem('userId');
    const refreshToken = localStorage.getItem('refreshToken');
    if (!token || !userId) return null;
    return { token, userId, refreshToken };
  } catch {
    return null;
  }
}

/** Writes tokens + userId to localStorage. Use this after login/register. */
export function persistAuth({ accessToken, refreshToken, userId }) {
  try {
    if (accessToken) localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    if (userId) localStorage.setItem('userId', userId);
  } catch {
    /* storage quotas / private mode — ignore */
  }
}

/** Clears tokens + userId from localStorage. Use on logout. */
export function clearAuth() {
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
  } catch {
    /* ignore */
  }
}

/**
 * Thin wrapper around fetch that adds the Authorization header. Does NOT
 * handle refresh-token rotation — if you need that, add a custom retry here
 * against API.refresh().
 */
export async function authFetch(url, options = {}) {
  const auth = getAuth();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (auth?.token) {
    headers.Authorization = `Bearer ${auth.token}`;
  }
  return fetch(url, { ...options, headers });
}

/**
 * Fetches the full user profile and tells you whether it's complete enough
 * to land on the dashboard. Used by whatever route guard you set up on the
 * web landing/home page.
 *
 * Returns null if unauthenticated or the request fails — callers should
 * treat null as "user not logged in" and show the landing page.
 */
export async function fetchProfileAndCompletionStatus() {
  const auth = getAuth();
  if (!auth) return null;
  try {
    const resp = await authFetch(API.userById(auth.userId), { method: 'GET' });
    if (!resp.ok) return null;
    const profile = await resp.json();
    return {
      profile,
      profileComplete: !!profile?.profileComplete,
    };
  } catch {
    return null;
  }
}